import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import apiService from '../services/apiService';

export default function ServicePlans() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    active: true,
    interval_unit: 'monthly',
    interval_count: 1,
    default_scheduled_time: '09:00',
    job_name_template: 'Service — {{client_name}}',
    default_notes: '',
    default_price: '',
    default_currency: 'GBP',
    reminder_days_before: 7,
    billing_enabled: false,
    vat_rate: 20,
  });

  async function fetchItems() {
    setError('');
    setLoading(true);
    try {
      const res = await apiService.get('service-plans?include_inactive=1');
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchItems();
  }, []);

  function openCreate() {
    setEditingId(null);
    setForm({
      name: '',
      description: '',
      active: true,
      interval_unit: 'monthly',
      interval_count: 1,
      default_scheduled_time: '09:00',
      job_name_template: 'Service — {{client_name}}',
      default_notes: '',
      default_price: '',
      default_currency: 'GBP',
      reminder_days_before: 7,
      billing_enabled: false,
      vat_rate: 20,
    });
    setDialogOpen(true);
  }

  async function openEdit(id) {
    try {
      const p = await apiService.get(`service-plans/${id}`);
      setEditingId(id);
      setForm({
        name: p.name || '',
        description: p.description || '',
        active: p.active !== false,
        interval_unit: p.interval_unit === 'weekly' ? 'weekly' : 'monthly',
        interval_count: p.interval_count ?? 1,
        default_scheduled_time: p.default_scheduled_time || '09:00',
        job_name_template: p.job_name_template || 'Service — {{client_name}}',
        default_notes: p.default_notes || '',
        default_price: p.default_price ?? '',
        default_currency: p.default_currency || 'GBP',
        reminder_days_before: p.reminder_days_before ?? 7,
        billing_enabled: !!p.billing_enabled,
        vat_rate: p.vat_rate ?? 20,
      });
      setDialogOpen(true);
    } catch (e) {
      setError(e.message || 'Failed to load plan');
    }
  }

  async function handleSave() {
    setBusy(true);
    setError('');
    try {
      const payload = {
        ...form,
        interval_count: Number(form.interval_count) || 1,
        reminder_days_before: Number(form.reminder_days_before) || 7,
        default_price: form.default_price === '' ? null : Number(form.default_price),
      };
      if (!payload.name.trim()) {
        setError('Name is required');
        setBusy(false);
        return;
      }
      if (editingId) {
        await apiService.put(`service-plans/${editingId}`, payload);
      } else {
        await apiService.post('service-plans', payload);
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this service plan?')) return;
    try {
      await apiService.delete(`service-plans/${id}`);
      await fetchItems();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Service plans
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ textTransform: 'none' }}>
          New plan
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Recurring service templates. Assign plans to clients from the client editor. Jobs are created daily via{' '}
        <code>php artisan subscriptions:process</code>.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((it) => (
            <Card key={it.id}>
              <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 900 }}>{it.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Every {it.interval_count} {it.interval_unit === 'weekly' ? 'week(s)' : 'month(s)'} · reminder{' '}
                    {it.reminder_days_before}d before
                    {it.billing_enabled ? <Chip size="small" label="Billing on" color="success" sx={{ ml: 1, height: 22 }} /> : null}
                    {!it.active ? <Chip size="small" label="Inactive" sx={{ ml: 1, height: 22 }} /> : null}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEdit(it.id)} sx={{ textTransform: 'none' }}>
                  Edit
                </Button>
                <IconButton onClick={() => handleDelete(it.id)}>
                  <DeleteIcon />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 800 }}>{editingId ? 'Edit plan' : 'New service plan'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Interval</InputLabel>
              <Select label="Interval" value={form.interval_unit} onChange={(e) => setForm((f) => ({ ...f, interval_unit: e.target.value }))}>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Every N intervals"
              type="number"
              value={form.interval_count}
              onChange={(e) => setForm((f) => ({ ...f, interval_count: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Default time"
              value={form.default_scheduled_time}
              onChange={(e) => setForm((f) => ({ ...f, default_scheduled_time: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Job name template"
              value={form.job_name_template}
              onChange={(e) => setForm((f) => ({ ...f, job_name_template: e.target.value }))}
              fullWidth
              sx={{ gridColumn: { sm: '1 / -1' } }}
            />
            <TextField
              label="Default price"
              type="number"
              value={form.default_price}
              onChange={(e) => setForm((f) => ({ ...f, default_price: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Reminder days before"
              type="number"
              value={form.reminder_days_before}
              onChange={(e) => setForm((f) => ({ ...f, reminder_days_before: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.billing_enabled}
                  onChange={(e) => setForm((f) => ({ ...f, billing_enabled: e.target.checked }))}
                />
              }
              label="Invoice each billing cycle"
            />
            <TextField
              label="VAT %"
              type="number"
              value={form.vat_rate}
              onChange={(e) => setForm((f) => ({ ...f, vat_rate: e.target.value }))}
              fullWidth
              disabled={!form.billing_enabled}
            />
            <TextField
              label="Default notes"
              value={form.default_notes}
              onChange={(e) => setForm((f) => ({ ...f, default_notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              sx={{ gridColumn: { sm: '1 / -1' } }}
            />
          </Box>
          <FormControlLabel
            control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />}
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={busy}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
